import { lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiReachable } from '../../lib/api/connectivity.js';
import { LanguageMenu } from './LanguageMenu.js';
import { Lockup } from './Lockup.js';
import './layouts.css';

// Shown only while the API is unreachable, so loaded only then.
const BackSoonNotice = lazy(() =>
  import('./BackSoonNotice.js').then((module) => ({ default: module.BackSoonNotice })),
);

/**
 * The layout of public pages (the 404 and error pages):
 * a mist page with the lockup and the language switch, the content, and a
 * short footer. Sign-in pages use `AuthLayout` instead.
 *
 * @example
 * <PublicLayout><PageHeader title="Sign in" /></PublicLayout>
 */
export function PublicLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const reachable = useApiReachable();
  return (
    <div className="public">
      <a className="skip-link" href="#main">
        {t('shell.skipToContent')}
      </a>
      <header className="public__bar">
        <Lockup />
        <LanguageMenu />
      </header>
      <main id="main" className="public__main" tabIndex={-1}>
        {children}
      </main>
      <footer className="public__footer">
        <span>{t('shell.footer.copyright', { year: new Date().getFullYear() })}</span>
      </footer>
      {reachable ? null : (
        <Suspense fallback={null}>
          <BackSoonNotice />
        </Suspense>
      )}
    </div>
  );
}
